'use client';

import { Stack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Field } from '../../../components/field';
import { Input } from '../../../components/input';
import { Textarea } from '../../../components/textarea';
import { useDraftOnOpen } from '../../application-general/useDraftOnOpen';
import { SettingsEditDialog } from '../../settings/SettingsEditDialog';
import {
  PROFILE_LIST_VALUE_ERROR,
  validateProfileListValue,
  type BusinessProfileEntityFormValues,
  type ProfileListValueError,
} from './business-profile.model';

export type BusinessProfileEntityNamespace =
  | 'designSystem.networkSettings.businessProfile.divisions'
  | 'designSystem.networkSettings.businessProfile.subsidiaries';

export type AddEntityDialogProps = {
  open: boolean;
  isCreate: boolean;
  editIndex: number | null;
  existingNames: string[];
  initialValues: BusinessProfileEntityFormValues;
  onOpenChange: (open: boolean) => void;
  onSave: (values: BusinessProfileEntityFormValues) => Promise<void>;
  isSaving: boolean;
  translationsNamespace: BusinessProfileEntityNamespace;
};

type EntityDraft = {
  values: BusinessProfileEntityFormValues;
  nameError: ProfileListValueError | null;
};

const NAME_ERROR_KEY = {
  [PROFILE_LIST_VALUE_ERROR.REQUIRED]: 'validation.nameRequired',
  [PROFILE_LIST_VALUE_ERROR.DUPLICATE]: 'validation.nameDuplicate',
} as const satisfies Record<ProfileListValueError, string>;

export function AddEntityDialog({
  open,
  isCreate,
  editIndex,
  existingNames,
  initialValues,
  onOpenChange,
  onSave,
  isSaving,
  translationsNamespace,
}: AddEntityDialogProps) {
  const t = useTranslations(translationsNamespace);
  const tCommon = useTranslations('designSystem.common');
  const [draft, setDraft] = useDraftOnOpen<EntityDraft>({
    open,
    value: { values: initialValues, nameError: null },
  });

  const updateField = ({
    field,
    value,
  }: {
    field: keyof BusinessProfileEntityFormValues;
    value: string;
  }) =>
    setDraft((previous) => ({
      values: { ...previous.values, [field]: value },
      nameError: field === 'name' ? null : previous.nameError,
    }));

  const handleSave = async () => {
    const nameError = validateProfileListValue({
      values: existingNames,
      draft: draft.values.name,
      editIndex,
    });

    if (nameError) {
      setDraft((previous) => ({ ...previous, nameError }));

      return;
    }

    await onSave({ ...draft.values, name: draft.values.name.trim() });
  };

  return (
    <SettingsEditDialog
      open={open}
      title={isCreate ? t('addTitle') : t('editTitle')}
      description={t('description')}
      onOpenChange={onOpenChange}
      onSubmit={() => void handleSave()}
      loading={isSaving}
      submitText={tCommon('saveChanges')}
      contentMaxW="xl"
    >
      <Stack gap={6}>
        <Field.Root invalid={draft.nameError !== null}>
          <Field.Label>{t('fields.name')}</Field.Label>
          <Input
            value={draft.values.name}
            onChange={(event) =>
              updateField({ field: 'name', value: event.target.value })
            }
            placeholder={t('fields.namePlaceholder')}
          />
          {draft.nameError ? (
            <Field.ErrorText>
              {t(NAME_ERROR_KEY[draft.nameError])}
            </Field.ErrorText>
          ) : null}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('fields.location')}</Field.Label>
          <Input
            value={draft.values.location}
            onChange={(event) =>
              updateField({ field: 'location', value: event.target.value })
            }
            placeholder={t('fields.locationPlaceholder')}
          />
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('fields.description')}</Field.Label>
          <Textarea
            value={draft.values.description}
            onChange={(event) =>
              updateField({ field: 'description', value: event.target.value })
            }
            rows={4}
            placeholder={t('fields.descriptionPlaceholder')}
          />
        </Field.Root>
      </Stack>
    </SettingsEditDialog>
  );
}
